// src/components/shared/Breadcrumbs.tsx
// Reusable breadcrumb navigation component following BrightPath design

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  homeHref?: string;
  showHome?: boolean;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items = [],
  homeHref = '/family/dashboard',
  showHome = true,
  className = '',
}) => {
  const location = useLocation();

  // Auto-generate breadcrumbs from path if no items provided
  const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => {
    if (items.length > 0) return items;

    const pathSegments = location.pathname
      .split('/')
      .filter(Boolean)
      .slice(1); // Skip 'family' prefix

    return pathSegments.map((segment, index) => {
      const href = '/family/' + pathSegments.slice(0, index + 1).join('/');
      const label = segment
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

      return {
        label,
        href: index < pathSegments.length - 1 ? href : undefined,
      };
    });
  }, [items, location.pathname]);

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-1.5 text-sm ${className}`}
    >
      {showHome && (
        <>
          <Link
            to={homeHref}
            className="flex items-center gap-1.5 text-warm-gray hover:text-sage-dark transition-colors duration-200 group"
          >
            <span className="w-7 h-7 rounded-md bg-cream-dark group-hover:bg-sage/10 flex items-center justify-center transition-colors duration-200">
              <Home className="w-4 h-4" />
            </span>
            <span className="hidden sm:inline font-medium">Home</span>
          </Link>
          {breadcrumbItems.length > 0 && (
            <ChevronRight className="w-4 h-4 text-warm-gray/50 flex-shrink-0" />
          )}
        </>
      )}

      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;

        return (
          <React.Fragment key={index}>
            {item.href && !isLast ? (
              <Link
                to={item.href}
                className="flex items-center gap-1.5 text-warm-gray hover:text-sage-dark transition-colors duration-200 font-medium truncate max-w-[150px] sm:max-w-[200px]"
              >
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </Link>
            ) : (
              <span
                className={`flex items-center gap-1.5 truncate max-w-[150px] sm:max-w-[250px] ${
                  isLast
                    ? 'text-navy font-semibold'
                    : 'text-warm-gray font-medium'
                }`}
              >
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </span>
            )}

            {!isLast && (
              <ChevronRight className="w-4 h-4 text-warm-gray/50 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// Compact version for tight spaces
export const BreadcrumbsCompact: React.FC<BreadcrumbsProps> = ({
  items = [],
  homeHref = '/family/dashboard',
  className = '',
}) => {
  const visibleItems = items.length > 2 
    ? [items[0], { label: '...', href: undefined }, items[items.length - 1]]
    : items;

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-1 text-xs ${className}`}
    >
      <Link
        to={homeHref}
        className="text-warm-gray hover:text-sage-dark transition-colors"
      >
        <Home className="w-3.5 h-3.5" />
      </Link>

      {visibleItems.map((item, index) => (
        <React.Fragment key={index}>
          <ChevronRight className="w-3 h-3 text-warm-gray/40" />
          {item.href ? (
            <Link
              to={item.href}
              className="text-warm-gray hover:text-sage-dark transition-colors truncate max-w-[100px]"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-navy font-medium truncate max-w-[100px]">
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumbs;
